import React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useTranslation } from 'react-i18next'
import './DateSheetShell.scss'

export interface DateSheetShellProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Repeated task title shown at the top for context. Falls back to "Neue Aufgabe". */
  title?: string
  /** The stacked picker body (rendered into the scrollable area). */
  children: React.ReactNode
}

/**
 * DateSheetShell — shared bottom-sheet chrome for the date pickers (WhenDropdown /
 * DeadlineDropdown) when the window is too narrow for the 500px popover.
 *
 * Built on Radix Dialog but positioned `absolute` inside `.main-layout__body`
 * (which is `position: relative` and sits BELOW the titlebar), so the sheet never
 * covers the titlebar / mac traffic lights. `modal={false}` keeps the titlebar
 * (ProjectSwitcher, window controls) fully interactive; dismissal still works via
 * Esc + outside-press (Radix DismissableLayer). A max-height leaves a dimmed
 * "peek" of the list above the sheet; the body scrolls, header + footer stay put.
 */
export const DateSheetShell: React.FC<DateSheetShellProps> = ({ open, onOpenChange, title, children }) => {
  const { t } = useTranslation()
  // Portal into the content area below the titlebar so the sheet + scrim never
  // cover the titlebar (mac traffic lights live there). Falls back to body.
  const container = (typeof document !== 'undefined'
    ? (document.querySelector('.main-layout__body') as HTMLElement | null)
    : null) ?? undefined

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <Dialog.Portal container={container}>
        {/* Own scrim — Radix `Dialog.Overlay` renders null in non-modal mode.
            Click to dismiss; sits below the panel, above the list. The
            `data-task-dropdown-backdrop` marker makes the expanded TaskEntry's
            outside-click guard treat this like a dropdown (so confirming/clicking
            inside the sheet doesn't collapse the underlying task). */}
        <div
          className="date-sheet-overlay"
          data-task-dropdown-backdrop
          data-state={open ? 'open' : 'closed'}
          onClick={() => onOpenChange(false)}
        />
        {/* `data-task-dropdown`: same marker the floating dropdown carries, so the
            TaskEntry pointerdown/Enter guards skip it and keep the task expanded. */}
        <Dialog.Content className="date-sheet" data-task-dropdown aria-describedby={undefined}>
          <div className="date-sheet__grabber" aria-hidden />
          <div className="date-sheet__header">
            <Dialog.Title className="date-sheet__title">
              {title?.trim() ? title : t('taskCreation.newTask')}
            </Dialog.Title>
          </div>
          <div className="date-sheet__body">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
